import React from 'react';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import Link from '@amzn/awsui-components-console/link';
import Spinner from '@amzn/awsui-components-console/spinner';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import {
  SUBMISSION_PENDING_BANNER_BODY_KEY,
  SUBMISSION_PENDING_BANNER_BODY_NO_ADDRESS_KEY,
  SUBMISSION_PENDING_BANNER_HEADER_KEY,
  SUBMISSION_PENDING_NOTE_KEY,
} from '../constants';
import { TRACK_MY_TRANSLATIONS_PATH } from 'src/common/routes';
import { INLINE_SLOT, splitAroundSlot } from 'src/i18n/inlineSlot';
import { useTranslation } from 'src/i18n/shim/useTranslation';

interface InterimSubmissionBannerProps {
  /** Address the confirmation email will be sent to, named in the copy. */
  notificationEmail?: string;
  /** Client-side navigation to Track Translations; falls back to plain text when absent. */
  onTrackTranslationsClick?: () => void;
}

/**
 * The durable "visible pending state" of the interim-period flow. Rendered
 * whenever a submission is awaiting TRANSCOM confirmation — a condition derived
 * from server state (describeStringGroup answering StringGroupNotReadyException),
 * so this banner reappears on reload, in a new tab and on another device until
 * the submission is confirmed.
 *
 * Indeterminate by design: it shows a spinner, never a percentage or stage.
 */
const InterimSubmissionBanner: React.FC<InterimSubmissionBannerProps> = ({
  notificationEmail,
  onTrackTranslationsClick,
}) => {
  const { t } = useTranslation();
  const trimmedEmail = notificationEmail?.trim();
  // User data must never appear in a resource file, so the address is always an ICU parameter.
  const body =
    trimmedEmail && trimmedEmail.length > 0
      ? t(SUBMISSION_PENDING_BANNER_BODY_KEY, {
          recipient: trimmedEmail,
          trackLink: INLINE_SLOT,
        })
      : t(SUBMISSION_PENDING_BANNER_BODY_NO_ADDRESS_KEY, {
          trackLink: INLINE_SLOT,
        });
  const [beforeLink, afterLink] = splitAroundSlot(body);

  return (
    <Alert
      type="info"
      header={
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          <Spinner />
          <span>{t(SUBMISSION_PENDING_BANNER_HEADER_KEY)}</span>
        </SpaceBetween>
      }
    >
      <SpaceBetween size="xs">
        <Box variant="p">
          {beforeLink}
          {onTrackTranslationsClick ? (
            <Link
              href={TRACK_MY_TRANSLATIONS_PATH}
              onFollow={(e) => {
                e.preventDefault();
                onTrackTranslationsClick();
              }}
            >
              {t('sgd-track-translations')}
            </Link>
          ) : (
            t('sgd-track-translations')
          )}
          {afterLink}
        </Box>
        <Box variant="small" color="text-status-inactive">
          {t(SUBMISSION_PENDING_NOTE_KEY)}
        </Box>
      </SpaceBetween>
    </Alert>
  );
};

export default InterimSubmissionBanner;
